import { Theme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { makeStyles } from '@mui/styles';

import { csTokens, secondaryText } from 'components/CallScribe/callScribeTheme';

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    padding: theme.spacing(2.5),
    boxSizing: 'border-box',
    width: '100%',
    height: '100%',
    minHeight: 0,
    overflowY: 'auto',
    background: csTokens(theme).bg,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1.5),
  },
  title: {
    ...theme.typography.h6,
    fontWeight: 600,
    color: theme.palette.text.primary,
  },
  timeEntrySpan: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    ...theme.typography.caption,
    color: secondaryText(theme),
  },
  retryButton: {
    '&.MuiButton-root': {
      textTransform: 'none',
      fontWeight: 500,
      borderRadius: 8,
      marginTop: theme.spacing(1),
    },
  },
  emptyState: {
    padding: theme.spacing(3),
    textAlign: 'center',
    color: secondaryText(theme),
  },
  errorState: {
    padding: theme.spacing(1.5),
    background: alpha(theme.palette.error.main, 0.12),
    color: theme.palette.error.main,
    borderRadius: 8,
    ...theme.typography.body2,
  },
  callRow: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    padding: theme.spacing(1.5),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  callRowHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  meta: {
    ...theme.typography.caption,
    color: secondaryText(theme),
  },
  recording: {
    width: '100%',
  },
  softphone: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    background: theme.palette.background.paper,
    padding: theme.spacing(1.5),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  softphoneRow: {
    display: 'flex',
    gap: theme.spacing(1),
    alignItems: 'center',
  },
  timeEntryCard: {
    background: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    padding: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
    color: theme.palette.text.primary,
    // MUI labels default to text.secondary, which this app pins to white in both
    // modes, so on the light card every field label was white on white.
    '& .MuiInputLabel-root': {
      color: secondaryText(theme),
    },
    '& .MuiInputLabel-root.Mui-focused': {
      color: theme.palette.text.primary,
    },
  },
  timeEntryTitle: {
    ...theme.typography.body1,
    fontWeight: 600,
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(0.5),
  },
  timeEntryActions: {
    display: 'flex',
    gap: theme.spacing(1),
    justifyContent: 'flex-end',
  },
  actionButton: {
    '&.MuiButton-root': {
      textTransform: 'none',
      fontWeight: 500,
    },
  },
  reviewPanel: {
    background: theme.palette.action.hover,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    padding: theme.spacing(1.25, 1.5),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  readOnlyNoteLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.75),
  },
  readOnlyNoteBadge: {
    ...theme.typography.caption,
    fontWeight: 600,
    padding: '0 6px',
    borderRadius: 4,
    border: `1px solid ${theme.palette.divider}`,
    color: secondaryText(theme),
  },
  readOnlyNoteBody: {
    ...theme.typography.body2,
    color: theme.palette.text.primary,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
    background: theme.palette.action.hover,
    border: `1px dashed ${theme.palette.divider}`,
    borderRadius: 8,
    padding: theme.spacing(1),
    minWidth: 0,
  },
  reviewSectionLabel: {
    ...theme.typography.caption,
    fontWeight: 600,
    color: secondaryText(theme),
  },
  reviewSectionBody: {
    ...theme.typography.body2,
    whiteSpace: 'pre-wrap',
    lineHeight: 1.45,
    color: theme.palette.text.primary,
  },
  transcriptBody: {
    ...theme.typography.caption,
    whiteSpace: 'pre-wrap',
    lineHeight: 1.5,
    maxHeight: 240,
    overflowY: 'auto',
    background: theme.palette.background.paper,
    color: theme.palette.text.primary,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 4,
    padding: theme.spacing(1, 1.25),
  },
  transcriptToggle: {
    alignSelf: 'flex-start',
    ...theme.typography.caption,
    fontWeight: 500,
    color: theme.palette.text.primary,
    textDecoration: 'underline',
    background: 'none',
    border: 'none',
    padding: 0,
    cursor: 'pointer',
  },
}));

export default useStyles;
